import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { Link } from "react-router-dom";

import styles from "./kit.module.css";

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/* --------------------------------------------------------------- page ---- */

/** Title, optional line of explanation, and whatever belongs on the right. */
export function PageHead({
  title,
  note,
  actions,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.pageHead}>
      <div>
        <h1 className={styles.pageTitle}>{title}</h1>
        {note && <p className={styles.pageNote}>{note}</p>}
      </div>
      {actions}
    </div>
  );
}

/**
 * Where you are.
 *
 * A fixture can sit six levels deep — Games › sport › event › round › heat —
 * and stage 1 found that not showing the path was a real failure, not a
 * nicety. So this is a primitive, not something each screen improvises.
 */
export function Crumbs({ trail }: { trail: { label: string; href?: string }[] }) {
  return (
    <nav className={styles.crumbs} aria-label="Where you are">
      {trail.map((step, i) => (
        <span key={`${step.label}-${i}`}>
          {i > 0 && <span className={styles.crumbSep}>›</span>}{" "}
          {step.href ? <Link to={step.href}>{step.label}</Link> : step.label}
        </span>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------ section ---- */

/**
 * The unit the one-page sport editor is built from.
 *
 * Sections are separated by a rule and nothing else — no card, no shadow. A
 * long page of stacked cards reads as a pile of unrelated things, which is
 * exactly what the old definition screen looked like.
 */
export function Section({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <div>
          <h2 className={styles.sectionTitle}>{title}</h2>
          {note && <p className={styles.sectionNote}>{note}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx(styles.card, className)}>{children}</div>;
}

/** The row above a list: search and filters on the left, actions on the
 * right. `<Spacer />` divides the two. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className={styles.toolbar}>{children}</div>;
}

/** A gapped column. For a caller assembling parts that carry no spacing of their own
 *  — which is all of them, since a component that sets its own outer margin has an
 *  opinion about a layout it cannot see. */
export function Stack({ children }: { children: ReactNode }) {
  return <div className={styles.stack}>{children}</div>;
}

export function Spacer() {
  return <span className={styles.spacer} />;
}

/**
 * A page that could not load what it came for.
 *
 * Its own primitive because 27 screens were each reaching into a leftover
 * stylesheet for one class, which is how that stylesheet outlived every screen
 * it was written for. It is gone now.
 */
export function Problem({ children }: { children: ReactNode }) {
  return (
    <Card>
      <p className={styles.problem} role="alert">
        {children}
      </p>
    </Card>
  );
}

/* --------------------------------------------------------------- stat ---- */

/**
 * A count, what it counts, and a way in.
 *
 * The sport overview and the dashboard were each assembling one out of six
 * classes from a leftover stylesheet, which is how six classes end up in a
 * leftover stylesheet. A stat that links somewhere is a link; one that does not
 * is a div, and the difference is visible on hover rather than assumed.
 */
export function Stat({
  label,
  value,
  note,
  icon,
  href,
}: {
  label: string;
  value: ReactNode;
  note?: string;
  icon?: ReactNode;
  href?: string;
}) {
  const body = (
    <>
      <span className={styles.statHead}>
        {label}
        {icon && <span className={styles.statIcon}>{icon}</span>}
      </span>
      <span className={styles.statValue}>{value}</span>
      {note && <span className={styles.statNote}>{note}</span>}
    </>
  );
  return href ? (
    <Link to={href} className={styles.stat}>
      {body}
    </Link>
  ) : (
    <div className={styles.stat}>{body}</div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className={styles.statGrid}>{children}</div>;
}

/* --------------------------------------------------------------- pill ---- */

type PillTone = "neutral" | "accent" | "good" | "warn" | "bad" | "live";

const PILL_CLASS: Record<PillTone, string> = {
  neutral: styles.pillNeutral,
  accent: styles.pillAccent,
  good: styles.pillGood,
  warn: styles.pillWarn,
  bad: styles.pillBad,
  live: styles.pillLive,
};

/**
 * A state, shown as shape and colour together.
 *
 * `dot` adds a marker so the state survives a screenshot, a printout, and
 * anyone who cannot tell the two colours apart. Colour alone is never the only
 * carrier.
 */
export function Pill({
  tone = "neutral",
  dot = false,
  children,
}: {
  tone?: PillTone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={cx(styles.pill, PILL_CLASS[tone])}>
      {dot && <span className={styles.dot} />}
      {children}
    </span>
  );
}

/* -------------------------------------------------------------- table ---- */

/** A table in its own box, scrolling sideways inside it so the page body
 * never does. */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className={styles.tableWrap}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>{children}</table>
      </div>
    </div>
  );
}

export function Th({
  num,
  className,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { num?: boolean }) {
  return <th className={cx(num && styles.num, className)} {...rest} />;
}

export function Td({
  num,
  strong,
  quiet,
  className,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & {
  num?: boolean;
  strong?: boolean;
  quiet?: boolean;
}) {
  return (
    <td
      className={cx(num && styles.num, strong && styles.strong, quiet && styles.quiet, className)}
      {...rest}
    />
  );
}

/* -------------------------------------------------------------- split ---- */

/**
 * A page of two halves: the work on the left, something that watches it on the
 * right.
 *
 * The rail sticks, because what it holds is the reason the left half is being
 * filled in — a preview you have to scroll back up to see is a preview nobody
 * looks at. On a narrow screen it stops being a rail and goes underneath.
 */
export function Split({ children, rail }: { children: ReactNode; rail: ReactNode }) {
  return (
    <div className={styles.split}>
      <div className={styles.splitMain}>{children}</div>
      <aside className={styles.splitRail}>{rail}</aside>
    </div>
  );
}

/* -------------------------------------------------------------- steps ---- */

/**
 * Where you are in a form that asks several things.
 *
 * Numbered and clickable, and the ones behind you are marked done — a form
 * that shows one field and no shape leaves you unable to tell a short task
 * from a long one, which is what makes people abandon it.
 */
export function Steps({
  steps,
  at,
  onGo,
}: {
  steps: string[];
  at: number;
  onGo: (index: number) => void;
}) {
  return (
    <ol className={styles.steps} aria-label="Steps">
      {steps.map((label, i) => (
        <li key={label}>
          <button
            type="button"
            aria-current={i === at ? "step" : undefined}
            className={cx(styles.step, i === at && styles.stepOn, i < at && styles.stepDone)}
            onClick={() => onGo(i)}
          >
            <span className={styles.stepNumber}>{i < at ? "✓" : i + 1}</span>
            {label}
          </button>
        </li>
      ))}
    </ol>
  );
}

/* -------------------------------------------------------------- empty ---- */

/**
 * What a list says when it holds nothing.
 *
 * Always carries the action that fills it. "Nothing is a dead end" is one of
 * the six rules, and an empty list with no way forward is the commonest dead
 * end there is.
 */
export function Empty({
  title,
  note,
  action,
}: {
  title: string;
  note?: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>{title}</p>
      {note && <p className={styles.emptyNote}>{note}</p>}
      {action && <div className={styles.emptyAction}>{action}</div>}
    </div>
  );
}
